import React, { useState, useEffect } from 'react';
import { Settings, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { getScoringWeights, updateScoringWeights, getUsers } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const roleLabels = {
  admin: 'System Administrator',
  hospital_admin: 'Hospital Admin',
  pharmacy_director: 'Pharmacy Director',
  procurement_lead: 'Procurement Lead',
  clinical_reviewer: 'Clinical Reviewer',
  analyst: 'Analyst / Viewer'
};

export default function AdminPage() {
  const { user } = useAuth();
  const [weights, setWeights] = useState({
    efficacy: 25,
    regulatory: 20,
    availability: 15,
    distribution: 10,
    manufacturing: 10,
    study_support: 10,
    procurement_fit: 10
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    async function fetchData() {
      try {
        const [weightsRes, usersRes] = await Promise.all([
          getScoringWeights(),
          getUsers()
        ]);
        
        const { type, ...weightValues } = weightsRes.data;
        setWeights(prev => ({ ...prev, ...weightValues }));
        setUsers(usersRes.data.users || []);
      } catch (error) {
        console.error('Failed to fetch admin data:', error);
        if (error.response?.status === 403) {
          setError('Admin access required');
        }
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const handleWeightChange = (key, value) => {
    const numValue = parseInt(value) || 0;
    setWeights(prev => ({ ...prev, [key]: numValue }));
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    const total = Object.values(weights).reduce((a, b) => a + b, 0);
    if (total !== 100) {
      setError(`Weights must sum to 100. Current total: ${total}`);
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      await updateScoringWeights(weights);
      setSuccess('Scoring weights updated successfully');
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to update weights');
    } finally {
      setSaving(false);
    }
  };

  const total = Object.values(weights).reduce((a, b) => a + b, 0);

  if (loading) {
    return (
      <div className="p-6" data-testid="admin-loading">
        <div className="skeleton h-8 w-48 mb-6" />
        <div className="skeleton h-96 rounded-xl" />
      </div>
    );
  }

  if (!user || !['admin', 'hospital_admin'].includes(user.role)) {
    return (
      <div className="p-6" data-testid="admin-forbidden">
        <div className="card-premium p-12 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Admin Access Required</p>
          <p className="text-sm text-slate-500 mt-1">You don't have permission to access this page</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="admin-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight mb-2">Admin Settings</h1>
        <p className="text-slate-500">Configure scoring weights and system parameters</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Scoring Weights */}
        <div className="card-premium p-6" data-testid="scoring-weights-section">
          <div className="flex items-center gap-2 mb-6">
            <Settings className="w-5 h-5 text-slate-400" />
            <h2 className="text-lg font-semibold text-slate-900">Scoring Weights</h2>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2 text-emerald-700 text-sm">
              <CheckCircle className="w-4 h-4" />
              {success}
            </div>
          )}

          <div className="space-y-4">
            {Object.entries(weights).map(([key, value]) => (
              <div key={key}>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium text-slate-700 capitalize">
                    {key.replace('_', ' ')}
                  </label>
                  <span className="text-sm text-slate-500">{value}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="50"
                  value={value}
                  onChange={(e) => handleWeightChange(key, e.target.value)}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                  data-testid={`weight-${key}`}
                />
              </div>
            ))}
          </div>

          <div className="mt-6 pt-4 border-t border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-slate-700">Total</span>
              <span className={`text-sm font-bold ${total === 100 ? 'text-emerald-600' : 'text-red-600'}`}>
                {total}%
              </span>
            </div>
            <button
              onClick={handleSave}
              disabled={saving || total !== 100}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="save-weights-btn"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Weights'}
            </button>
          </div>
        </div>

        {/* Users List */}
        <div className="card-premium p-6" data-testid="users-section">
          <h2 className="text-lg font-semibold text-slate-900 mb-6">System Users</h2>
          <div className="space-y-3">
            {users.map(u => (
              <div key={u.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">{u.name}</p>
                  <p className="text-sm text-slate-500">{u.email}</p>
                </div>
                <span className="badge-neutral">{roleLabels[u.role] || u.role}</span>
              </div>
            ))}
            {users.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-4">No users found</p>
            )}
          </div>
        </div>
      </div>

      {/* Weight Descriptions */}
      <div className="mt-6 card-premium p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Weight Descriptions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="font-medium text-slate-900">Efficacy (Default: 25%)</p>
            <p className="text-slate-600">Clinical trial data, bioequivalence studies, and therapeutic equivalence ratings</p>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="font-medium text-slate-900">Regulatory (Default: 20%)</p>
            <p className="text-slate-600">Approval status across target markets, FDA/EMA designations, compliance history</p>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="font-medium text-slate-900">Availability (Default: 15%)</p>
            <p className="text-slate-600">Current supply status, inventory levels, procurement accessibility</p>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="font-medium text-slate-900">Distribution (Default: 10%)</p>
            <p className="text-slate-600">Geographic reach, logistics network, delivery reliability</p>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="font-medium text-slate-900">Manufacturing (Default: 10%)</p>
            <p className="text-slate-600">Manufacturer credibility, GMP compliance, quality track record</p>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="font-medium text-slate-900">Study Support (Default: 10%)</p>
            <p className="text-slate-600">Quantity and quality of supporting clinical evidence</p>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="font-medium text-slate-900">Procurement Fit (Default: 10%)</p>
            <p className="text-slate-600">Alignment with hospital procurement requirements</p>
          </div>
        </div>
      </div>
    </div>
  );
}
